import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useFormikContext } from 'formik';
import { Grid, Button } from '@material-ui/core';
import { paymentMethod } from '@ourbranch/lookups';

import { PolicyStatus } from 'core/helpers/policy-status';
import { useDisabledState } from 'common/disabled-context';
import { useBasisTheoryToken } from 'common/hooks/use-basis-theory-token';
import { FormField } from 'core/components/form';
import { LabelTooltip } from 'core/components/label-tooltip';
import { useStore } from 'core/store';
import { useToast } from 'core/components/toast';
import addCard from 'core/assets/svg/add-card.svg';
import useSession from 'core/hooks/use-session';
import { CreditCardForm } from '../../credit-card-form';
import { maskingDots } from '../helpers';
import { StripeCustomerIdField } from '../stripe-customer-id-field';
import SendCreditCardLinksModal from '../send-credit-card-links-modal';
import { SecondaryPaymentMethod } from '../secondary-payment-method';
import { PolicyTabs } from 'core/store/account-store/policies-store/policy-store/policy-store';
import { useNormalizePaymentMethod } from '../hooks/use-normalize-payment-method';
import usePaymentMethodDisabled from '../hooks/use-payment-method-disabled';
import useStyles from '../billing-details-form.styles';

const CreditCards = ({ ccModalPage }) => {
  const classes = useStyles();
  const { isAgency, viewOnly } = useSession();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const toast = useToast();
  const {
    account: {
      id: accountId,
      policies: {
        policy: {
          policy: { id },
          addCreditCard,
          billingDetails,
          isCancelledOrInCancellation,
          status,
          setCurrentTab
        }
      }
    }
  } = useStore();
  const { getBasisTheoryCCToken } = useBasisTheoryToken();
  const [showAdd, setShowAdd] = useState(false);
  const [openCreditCardModal, setOpenCreditCardModal] = useState(false);
  const { setDisabled } = useDisabledState();
  // Payment-method updates stay available on cancelled policies (#34203); this only blocks on
  // permission / hold cards / imported-non-endorseable.
  const paymentMethodDisabled = usePaymentMethodDisabled();

  const allPaymentMethods = billingDetails.allPaymentMethods || [];

  const cancelled = status === PolicyStatus.Cancelled;

  useNormalizePaymentMethod(paymentMethod.CreditCard);

  const handleAddCard = async () => {
    try {
      const { token } = await getBasisTheoryCCToken();
      const {
        success,
        message,
        card: cardDetails
      } = await addCreditCard({
        accountId,
        policyId: id,
        basisTheoryCardToken: token,
        customerId: values.stripeCustomerId,
        isPrimary: true
      });
      const externalAgentLabelText =
        isCancelledOrInCancellation && isAgency
          ? 'To process payments, contact support.'
          : 'To process payments, go to payment and price tab.';
      toast.notify({
        type: success ? 'success' : 'error',
        message,
        label: externalAgentLabelText,
        action: () => setCurrentTab(PolicyTabs.PAYMENT),
        durationInSeconds: 10
      });
      setShowAdd(false);
      if (success && !isCancelledOrInCancellation) {
        setFieldValue('defaultCreditCard', {
          id: cardDetails.id,
          value: `${cardDetails.brand ? cardDetails.brand : ''} ${maskingDots}${cardDetails.last4}`
        });
        setFieldTouched('defaultCreditCard', true);
        const shouldRefreshCustomerId =
          !values.stripeCustomerId ||
          values.stripeCustomerId.startsWith('cd_') ||
          cardDetails.customerId !== values.stripeCustomerId;
        if (shouldRefreshCustomerId) {
          // if we didn't have a customer id or we created a new card with a chargedesk id, then we need to set the one we got back
          setFieldValue('stripeCustomerId', cardDetails.customerId);
          setFieldTouched('stripeCustomerId', true);
        }
      }
      if (cancelled) {
        setDisabled(true);
      }
    } catch (error) {
      toast.notify({
        type: 'error',
        message: `An error occurred. ${error.message.replace(':', '').trim()}`
      });
    }
  };

  const toggleAddCreditCardForm = () => {
    setShowAdd(!showAdd);
  };

  const toggleSendLinksModal = () => {
    setOpenCreditCardModal(!openCreditCardModal);
    setShowAdd(false);
  };

  const creditCardsOptions = allPaymentMethods
    .filter((method) => !!method.brand)
    .map((card) => ({
      id: card.id || '',
      value: `${card.brand ? card.brand : ''} ${maskingDots}${card.last4}`
    }));

  return (
    <>
      <Grid container className={classes.billingDetailsContainer} spacing={4} alignItems="center">
        <Grid item xs={4}>
          <LabelTooltip
            label="Primary Card Number"
            tooltip={{
              label: 'More Info',
              onHoverText: 'This is the primary card we’ll use to process payments for this policy.'
            }}
          >
            <FormField
              name="defaultCreditCard.id"
              type="select"
              mode="dark"
              options={creditCardsOptions}
              // Bypass the tab-wide disabled context (which blocks on cancellation) and re-impose only
              // the non-cancellation guards, so the primary card can be changed on a cancelled
              // policy (#34203).
              ignoreGlobalDisabledState
              disabled={!creditCardsOptions.length || paymentMethodDisabled}
              fast={false}
              permissions={{ isLicensedAction: false }}
            />
          </LabelTooltip>
        </Grid>
        <StripeCustomerIdField />

        {/* -- Buttons that trigger forms and modals below -- */}

        {/* This "Add new card" shows for all staff users except view only users and allows users to send a link to add a new card or manually add a card */}
        <Button
          onClick={toggleSendLinksModal}
          variant="contained"
          color="secondary"
          className={classes.addCardButton}
          disabled={viewOnly}
        >
          <img src={addCard} alt="Add card" className={classes.addCardImage} />
          Add new card
        </Button>
      </Grid>

      {/* CC Form and modal that open/close based on buttons above */}
      {showAdd && isAgency && (
        <Grid container className={classes.billingDetailsContainer}>
          {showAdd && (
            <CreditCardForm
              onSubmit={handleAddCard}
              close={toggleAddCreditCardForm}
              buttonText="Add Card"
              showCloseButton
            />
          )}
        </Grid>
      )}
      {openCreditCardModal && (
        <SendCreditCardLinksModal
          open={openCreditCardModal}
          onClose={toggleSendLinksModal}
          onAddCard={handleAddCard}
          defaultPage={ccModalPage}
          isCancelledOrInCancellation={isCancelledOrInCancellation}
        />
      )}
      <Grid container className={classes.billingDetailsContainer} spacing={4}>
        {!viewOnly && <SecondaryPaymentMethod />}
      </Grid>
    </>
  );
};

CreditCards.propTypes = {
  ccModalPage: PropTypes.number.isRequired
};

export default observer(CreditCards);
